import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams } from '@tanstack/react-router';
import {
  Banner, Button, Card, Details, Dialog, List, ListItem, Select, StatusChip, TextField,
  TopAppBar, useSnackbar,
} from '@meshble/molo';
import { api, must, mustOk } from '../lib/api';
import { t } from '../lib/lingua';
import { quandoRelativo } from '../lib/attivita';
import { BarraUtente } from '../shell';
import { Globe, Shield, Warn } from '../icons';
import { Anello, CaricoSezione } from '../brand';
import { TokenAPI } from './token-api';
import { usePermessi } from '../session';

/**
 * Le impostazioni dell'organizzazione.
 *
 * Il dominio sotto cui nascono le sue istanze con il token che permette a
 * Molo di scriverne i record, la credenziale con cui l'hub legge il codice
 * Enterprise di Odoo, il progetto OVH, dove arrivano gli avvisi e i token API. È roba che si guarda una volta e poi si dimentica,
 * quindi sta in una pagina sua e non in mezzo alle altre.
 */

export function useDNS(slug: string) {
  return useQuery({
    queryKey: ['org-dns', slug],
    queryFn: () => must(api.GET('/api/v1/orgs/{slug}/dns', { params: { path: { slug } } })),
  });
}

export function ImpostazioniPage() {
  const { slug } = useParams({ from: '/shell/orgs/$slug/impostazioni' });
  const q = useDNS(slug);
  // La pagina si apre con uno qualunque dei tre permessi (shell.tsx), e ogni
  // sezione con il suo: una che non si può leggere risponderebbe 403.
  const puo = usePermessi(slug);

  return (
    <>
      <TopAppBar title={t('Impostazioni')} actions={<BarraUtente slug={slug} />} />
      <CaricoSezione attiva={q.isPending} />
      <div className="md-content">
        <div className="md-stack" style={{ gap: 'var(--md-sys-spacing-5)' }}>
          {q.error && puo('credential.manage') && (
            <Banner tone="error" title={t('Non riesco a leggere le impostazioni')}>{q.error.message}</Banner>
          )}
          {puo('credential.manage') && <Dominio slug={slug} />}
          {puo('enterprise.manage') && <Enterprise slug={slug} />}
          {puo('enterprise.manage') && <CatalogoModuli slug={slug} />}
          {puo('credential.manage') && <CredenzialiCloud slug={slug} />}
          {puo('alert.manage') && <Recapiti slug={slug} />}
          <TokenAPI slug={slug} />
        </div>
      </div>
    </>
  );
}

/**
 * Il dominio dell'organizzazione e il token che lo apre.
 *
 * Il token è anche la prova che il dominio è vostro: chi riesce a produrne uno
 * che apre quella zona, quella zona la controlla. Per questo non chiediamo un
 * record di verifica in più.
 */
function Dominio({ slug }: { slug: string }) {
  const qc = useQueryClient();
  const avvisa = useSnackbar();
  const q = useDNS(slug);
  const link = q.data;

  const [apri, setApri] = useState(false);
  const [dominio, setDominio] = useState('');
  const [token, setToken] = useState('');

  // Riaprendo il modulo si riparte da quello che c'è, non da un campo vuoto
  // che sembra dire «non è configurato niente».
  useEffect(() => {
    if (apri) setDominio(link?.domain ?? '');
  }, [apri, link?.domain]);

  const collega = useMutation({
    mutationFn: () =>
      must(api.PUT('/api/v1/orgs/{slug}/dns', {
        params: { path: { slug } },
        body: { domain: dominio.trim().toLowerCase(), token: token.trim() },
      })),
    onSuccess: (l) => {
      qc.invalidateQueries({ queryKey: ['org-dns', slug] });
      setApri(false);
      setToken('');
      avvisa(t('Le istanze nasceranno sotto {dominio}', { dominio: l.domain }));
    },
  });

  const scollega = useMutation({
    mutationFn: () => mustOk(api.DELETE('/api/v1/orgs/{slug}/dns', { params: { path: { slug } } })),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['org-dns', slug] });
      avvisa(t('Dominio scollegato: le istanze già create non cambiano indirizzo'));
    },
  });

  const pronto = dominio.trim().length > 2 && token.trim() !== '' && !collega.isPending;

  return (
    <Card variant="outlined">
      <div className="md-section-head">
        <h2 className="md-title-medium">{t('Dominio delle istanze')}</h2>
        <div className="md-stack__inline">
          {link?.domain && (
            <Button variant="text" disabled={scollega.isPending} onClick={() => scollega.mutate()}>
              {t('Scollega')}
            </Button>
          )}
          <Button variant="tonal" icon={<Globe />} onClick={() => setApri(true)}>
            {link?.domain ? t('Cambia') : t('Collega un dominio')}
          </Button>
        </div>
      </div>

      {scollega.error && (
        <Banner tone="error" title={t("Non l'ho scollegato")}>{(scollega.error as Error).message}</Banner>
      )}

      {!link?.domain ? (
        <p className="md-body-medium" style={{ margin: 0, color: 'var(--md-sys-color-on-surface-variant)' }}>
          {t("Nessun dominio collegato. Finché non ce n'è uno, un'istanza nuova nasce con un indirizzo che funziona solo dentro il server.")}
        </p>
      ) : (
        <>
          <div className="md-metric-grid">
            <div className="md-due-righe">
              <span className="md-label-medium">{t('Le istanze nascono come')}</span>
              <span className="md-mono">{t('nome.{dominio}', { dominio: link.domain })}</span>
            </div>
            <div className="md-due-righe">
              <span className="md-label-medium">{t('Il token apre la zona')}</span>
              <span className="md-mono">{link.zone || '—'}</span>
            </div>
            <div className="md-due-righe">
              <span className="md-label-medium">{t('Provato')}</span>
              <span>
                <StatusChip status="ready" label={t('Funziona')} />{' '}
                {link.checkedAt ? quandoRelativo(link.checkedAt) : ''}
              </span>
            </div>
          </div>

          {link.zone && link.zone !== link.domain && (
            <Banner tone="info" title={t('Il token ha potere su tutta la zona {zona}', { zona: link.zone })}>
              {t('Hai dichiarato {dominio}, ma i token di Cloudflare si limitano a una zona intera e non a un sottodominio. Quel token può quindi scrivere qualsiasi record di {zona}. Resta su Molo e non finisce mai sui server gestiti, ma è giusto che tu sappia cosa hai consegnato.', {
                dominio: <b>{link.domain}</b>,
                zona: <b>{link.zone}</b>,
              })}
            </Banner>
          )}
        </>
      )}

      <Details summary={t('Come si prepara il token')}>
        <div className="md-stack">
          <div className="md-banner" style={{ border: 'none', borderRadius: 'var(--md-sys-shape-corner-medium)' }}>
            <Shield />
            <div>
              <div className="md-title-small">{t('Il token è la prova che il dominio è vostro')}</div>
              <div className="md-body-small" style={{ marginTop: 2 }}>
                {t('Non chiediamo un record di verifica: chi riesce a produrre un token che apre quella zona, quella zona la controlla. Molo lo prova con Cloudflare prima di salvarlo, e se non apre il dominio che hai scritto lo rifiuta.')}
              </div>
            </div>
          </div>
          <p className="md-body-small" style={{ margin: 0 }}>
            {t('Nel pannello di Cloudflare, sotto «My Profile → API Tokens», crea un token con il permesso {permesso} e, in «Zone Resources», scegli {inclusione} selezionando solo la zona di questa azienda. Un token limitato a una zona è cieco su tutte le altre, anche se stanno nello stesso account.', {
              permesso: <b>Zone › DNS › Edit</b>,
              inclusione: <b>Include → Specific zone</b>,
            })}
          </p>
          <p className="md-body-small" style={{ margin: 0, color: 'var(--md-sys-color-on-surface-variant)' }}>
            {t("Il token resta su Molo, cifrato con la chiave dell'organizzazione. Sui server gestiti non scende mai: i certificati delle istanze si prendono con una verifica sulla porta 80, che non ha bisogno di credenziali DNS.")}
          </p>
        </div>
      </Details>

      <Dialog
        open={apri}
        onOpenChange={(v) => !v && setApri(false)}
        title={link?.domain ? t('Cambia dominio') : t('Collega un dominio')}
        description={t('Le istanze di questa azienda nasceranno come «nome.dominio». Il token viene provato prima di essere salvato.')}
        actions={
          <>
            <Button variant="text" onClick={() => setApri(false)}>{t('Annulla')}</Button>
            <Button
              variant="filled"
              icon={collega.isPending ? <Anello /> : undefined}
              disabled={!pronto}
              onClick={() => collega.mutate()}
            >
              {collega.isPending ? t('Provo…') : t('Prova e collega')}
            </Button>
          </>
        }
      >
        <form
          className="md-form"
          onSubmit={(e) => {
            e.preventDefault();
            if (pronto) collega.mutate();
          }}
        >
          {collega.error && (
            <Banner tone="error" title={t("Non l'ho collegato")}>{(collega.error as Error).message}</Banner>
          )}
          <TextField
            label={t('Dominio')}
            value={dominio}
            autoFocus
            onChange={(e) => setDominio(e.target.value.toLowerCase())}
            placeholder="meshble.com"
            help={t("Senza https e senza percorso. Un'istanza chiamata «acme» risponderà su acme.meshble.com.")}
          />
          <TextField
            label={t('Token API di Cloudflare')}
            value={token}
            onChange={(e) => setToken(e.target.value)}
            help={t('Zone › DNS › Edit, limitato alla sola zona di questo dominio. Non viene più mostrato dopo il salvataggio.')}
          />
        </form>
      </Dialog>
    </Card>
  );
}

/**
 * La credenziale con cui l'hub legge `odoo/enterprise`.
 *
 * `odoo/enterprise` è di Odoo, non nostro: non ci si possono appendere chiavi
 * di deploy né installarci sopra un'applicazione GitHub, perché entrambe le
 * cose le decide chi possiede il repository. Resta il token di un account a
 * cui Odoo, in quanto partner, ha dato accesso — e quell'account deve essere
 * dell'azienda, non di una persona.
 */
function Enterprise({ slug }: { slug: string }) {
  const qc = useQueryClient();
  const avvisa = useSnackbar();
  const q = useQuery({
    queryKey: ['org-enterprise', slug],
    queryFn: () => must(api.GET('/api/v1/orgs/{slug}/enterprise', { params: { path: { slug } } })),
  });
  const link = q.data;

  const [apri, setApri] = useState(false);
  const [token, setToken] = useState('');

  const collega = useMutation({
    mutationFn: () =>
      must(api.PUT('/api/v1/orgs/{slug}/enterprise', {
        params: { path: { slug } },
        body: { token: token.trim() },
      })),
    onSuccess: (l) => {
      qc.invalidateQueries({ queryKey: ['org-enterprise', slug] });
      setApri(false);
      setToken('');
      avvisa(t('Enterprise collegato con l’account {account}', { account: l.account }));
    },
  });

  const scollega = useMutation({
    mutationFn: () => mustOk(api.DELETE('/api/v1/orgs/{slug}/enterprise', { params: { path: { slug } } })),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['org-enterprise', slug] });
      avvisa(t('Credenziale scollegata: le istanze Enterprise che ci sono continuano a girare'));
    },
  });

  const pronto = token.trim() !== '' && !collega.isPending;

  return (
    <Card variant="outlined">
      <div className="md-section-head">
        <h2 className="md-title-medium">{t('Codice Enterprise')}</h2>
        <div className="md-stack__inline">
          {link?.hasToken && (
            <Button variant="text" disabled={scollega.isPending} onClick={() => scollega.mutate()}>
              {t('Scollega')}
            </Button>
          )}
          <Button variant="tonal" icon={<Shield />} onClick={() => setApri(true)}>
            {link?.hasToken ? t('Cambia') : t('Collega la credenziale')}
          </Button>
        </div>
      </div>

      {scollega.error && (
        <Banner tone="error" title={t('Non l’ho scollegata')}>{(scollega.error as Error).message}</Banner>
      )}

      {!link?.hasToken ? (
        <p className="md-body-medium" style={{ margin: 0, color: 'var(--md-sys-color-on-surface-variant)' }}>
          {t('Nessuna credenziale collegata. Finché non ce n’è una, le istanze di questa azienda nascono con il solo codice Community.')}
        </p>
      ) : (
        <div className="md-metric-grid">
          <div className="md-due-righe">
            <span className="md-label-medium">{t('Account GitHub')}</span>
            <span className="md-mono">{link.account || '—'}</span>
          </div>
          <div className="md-due-righe">
            <span className="md-label-medium">{t('Legge')}</span>
            <span className="md-mono">odoo/enterprise</span>
          </div>
          <div className="md-due-righe">
            <span className="md-label-medium">{t('Provata')}</span>
            <span>
              <StatusChip status="ready" label={t('Funziona')} />{' '}
              {link.checkedAt ? quandoRelativo(link.checkedAt) : ''}
            </span>
          </div>
        </div>
      )}

      <Details summary={t('Come si prepara la credenziale')}>
        <div className="md-stack">
          <div className="md-banner" style={{ border: 'none', borderRadius: 'var(--md-sys-shape-corner-medium)' }}>
            <Shield />
            <div>
              <div className="md-title-small">{t('L’account dev’essere dell’azienda, non di una persona')}</div>
              <div className="md-body-small" style={{ marginTop: 2 }}>
                {t('Legato a una persona sparisce con lei, e sparisce insieme la possibilità di aggiornare tutte le istanze Enterprise dei clienti nello stesso pomeriggio.')}
              </div>
            </div>
          </div>
          <p className="md-body-small" style={{ margin: 0 }}>
            {t('Su GitHub, entrando con l’account dell’azienda: «Settings → Developer settings → Personal access tokens → {classic}», con lo scope {scope}. Deve essere un token classic: quelli fine-grained danno accesso solo ai repository dell’account che li emette, e {repo} è di Odoo.', {
              classic: <b>Tokens (classic)</b>,
              scope: <b>repo</b>,
              repo: <b>odoo/enterprise</b>,
            })}
          </p>
          <p className="md-body-small" style={{ margin: 0, color: 'var(--md-sys-color-on-surface-variant)' }}>
            {t('Quell’account deve già vedere {repo}: è Odoo a concederlo ai partner. Molo prova la credenziale prima di salvarla, e se non lo vede la rifiuta dicendo quale account ha provato.', {
              repo: <b>odoo/enterprise</b>,
            })}
          </p>
        </div>
      </Details>

      <Dialog
        open={apri}
        onOpenChange={(v) => !v && setApri(false)}
        title={link?.hasToken ? t('Cambia credenziale') : t('Collega la credenziale Enterprise')}
        description={t('Il token viene provato su GitHub prima di essere salvato. L’account non si dichiara: lo legge da lì.')}
        actions={
          <>
            <Button variant="text" onClick={() => setApri(false)}>{t('Annulla')}</Button>
            <Button
              variant="filled"
              icon={collega.isPending ? <Anello /> : undefined}
              disabled={!pronto}
              onClick={() => collega.mutate()}
            >
              {collega.isPending ? t('Provo…') : t('Prova e collega')}
            </Button>
          </>
        }
      >
        <form
          className="md-form"
          onSubmit={(e) => {
            e.preventDefault();
            if (pronto) collega.mutate();
          }}
        >
          {collega.error && (
            <Banner tone="error" title={t('Non l’ho collegata')}>{(collega.error as Error).message}</Banner>
          )}
          <TextField
            label="Personal access token (classic)"
            value={token}
            autoFocus
            onChange={(e) => setToken(e.target.value)}
            placeholder="ghp_…"
            help={t('Scope repo, dell’account GitHub dell’azienda. Non viene più mostrato dopo il salvataggio.')}
          />
        </form>
      </Dialog>
    </Card>
  );
}

/**
 * Le fonti del catalogo dei moduli e il token con cui l'hub le legge.
 *
 * OCA c'è da subito perché è pubblica; le organizzazioni con i repository
 * privati vogliono il token. È di sola lettura e resta sull'hub: sui server i
 * privati arrivano con le deploy key, i pubblici in https.
 */
function CatalogoModuli({ slug }: { slug: string }) {
  const qc = useQueryClient();
  const avvisa = useSnackbar();
  const q = useQuery({
    queryKey: ['org-catalogo', slug],
    queryFn: () => must(api.GET('/api/v1/orgs/{slug}/catalog/settings', { params: { path: { slug } } })),
  });
  const link = q.data;

  const [apri, setApri] = useState(false);
  const [fonti, setFonti] = useState('');
  const [token, setToken] = useState('');

  const salva = useMutation({
    mutationFn: () =>
      must(api.PUT('/api/v1/orgs/{slug}/catalog/settings', {
        params: { path: { slug } },
        body: {
          owners: fonti.split(/[\s,]+/).filter(Boolean),
          ...(token.trim() ? { token: token.trim() } : {}),
        },
      })),
    onSuccess: (l) => {
      qc.invalidateQueries({ queryKey: ['org-catalogo', slug] });
      qc.invalidateQueries({ queryKey: ['catalogo', slug] });
      setApri(false);
      setToken('');
      avvisa(l.account ? t('Catalogo salvato, legge con l’account {account}', { account: l.account }) : t('Catalogo salvato'));
    },
  });

  const togli = useMutation({
    mutationFn: () => mustOk(api.DELETE('/api/v1/orgs/{slug}/catalog/token', { params: { path: { slug } } })),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['org-catalogo', slug] });
      qc.invalidateQueries({ queryKey: ['catalogo', slug] });
      avvisa(t('Token tolto: il catalogo mostra solo i repository pubblici'));
    },
  });

  return (
    <Card variant="outlined">
      <div className="md-section-head">
        <h2 className="md-title-medium">{t('Catalogo dei moduli')}</h2>
        <div className="md-stack__inline">
          {link?.hasToken && (
            <Button variant="text" disabled={togli.isPending} onClick={() => togli.mutate()}>
              {t('Togli il token')}
            </Button>
          )}
          <Button
            variant="tonal"
            onClick={() => {
              salva.reset();
              setFonti((link?.owners ?? []).join(', '));
              setApri(true);
            }}
          >
            {t('Cambia')}
          </Button>
        </div>
      </div>

      {togli.error && <Banner tone="error" title={t('Non l’ho tolto')}>{(togli.error as Error).message}</Banner>}

      <div className="md-metric-grid">
        <div className="md-due-righe">
          <span className="md-label-medium">{t('Organizzazioni GitHub')}</span>
          <span className="md-mono">{link?.owners?.length ? link.owners.join(', ') : '—'}</span>
        </div>
        <div className="md-due-righe">
          <span className="md-label-medium">Token</span>
          <span>
            {link?.hasToken ? (
              <>
                <StatusChip status="ready" label={link.account || t('Collegato')} />{' '}
                {link.checkedAt ? quandoRelativo(link.checkedAt) : ''}
              </>
            ) : (
              t('nessuno: solo repository pubblici')
            )}
          </span>
        </div>
      </div>

      <Details summary={t('Come si prepara il token')}>
        <div className="md-stack">
          <p className="md-body-small" style={{ margin: 0 }}>
            {t('Su GitHub: «Settings → Developer settings → Personal access tokens → {tipo}». Come proprietario l’organizzazione dei repository privati, accesso a tutti i repository, e due permessi in sola lettura: {metadata} e {contents}. I repository pubblici, OCA compresa, li legge qualunque token.', {
              tipo: <b>Fine-grained tokens</b>,
              metadata: <b>Metadata</b>,
              contents: <b>Contents</b>,
            })}
          </p>
          <p className="md-body-small" style={{ margin: 0, color: 'var(--md-sys-color-on-surface-variant)' }}>
            {t('Il token resta sull’hub e serve solo a elencare repository, rami e moduli. Sui server un privato arriva con la sua chiave di deploy, un pubblico in https.')}
          </p>
        </div>
      </Details>

      <Dialog
        open={apri}
        onOpenChange={(v) => !v && setApri(false)}
        title={t('Il catalogo dei moduli')}
        description={t('Da quali organizzazioni GitHub il catalogo prende i repository. Il token, se lo cambi, viene provato su GitHub prima di essere salvato.')}
        actions={
          <>
            <Button variant="text" onClick={() => setApri(false)}>{t('Annulla')}</Button>
            <Button
              variant="filled"
              icon={salva.isPending ? <Anello /> : undefined}
              disabled={salva.isPending}
              onClick={() => salva.mutate()}
            >
              {salva.isPending ? t('Salvo…') : t('Salva')}
            </Button>
          </>
        }
      >
        <form
          className="md-form"
          onSubmit={(e) => {
            e.preventDefault();
            if (!salva.isPending) salva.mutate();
          }}
        >
          {salva.error && <Banner tone="error" title={t('Non l’ho salvato')}>{(salva.error as Error).message}</Banner>}
          <TextField
            label={t('Organizzazioni GitHub')}
            value={fonti}
            autoFocus
            onChange={(e) => setFonti(e.target.value)}
            placeholder="OCA, meshble-hub"
            help={t('Separate da una virgola. Vuoto: niente catalogo.')}
          />
          <TextField
            label={t('Token fine-grained')}
            value={token}
            onChange={(e) => setToken(e.target.value)}
            placeholder={link?.hasToken ? t('Lascia vuoto per tenere quello che c’è') : 'github_pat_…'}
            help={t('Sola lettura: Metadata e Contents. Non viene più mostrato dopo il salvataggio.')}
          />
        </form>
      </Dialog>
    </Card>
  );
}

/**
 * Dove arrivano gli avvisi.
 *
 * Finché non ce n'è nessuno, Molo sa che qualcosa non va e non ha modo di
 * dirlo: gli avvisi restano nel pannello, e nel pannello ci si va quando si
 * sospetta già qualcosa.
 */
function Recapiti({ slug }: { slug: string }) {
  const qc = useQueryClient();
  const avvisa = useSnackbar();
  const q = useQuery({
    queryKey: ['org-channels', slug],
    queryFn: () => must(api.GET('/api/v1/orgs/{slug}/channels', { params: { path: { slug } } })),
  });
  const canali = q.data ?? [];

  const [apri, setApri] = useState(false);
  const [kind, setKind] = useState<'slack' | 'ntfy' | 'webhook'>('slack');
  const [label, setLabel] = useState('');
  const [url, setUrl] = useState('');

  const invalida = () => qc.invalidateQueries({ queryKey: ['org-channels', slug] });

  const aggiungi = useMutation({
    mutationFn: () =>
      must(api.POST('/api/v1/orgs/{slug}/channels', {
        params: { path: { slug } },
        body: { kind, label: label.trim(), url: url.trim() },
      })),
    onSuccess: (c) => {
      invalida();
      setApri(false);
      setLabel('');
      setUrl('');
      avvisa(t('{nome} collegato: il messaggio di prova è arrivato', { nome: c.label }));
    },
  });

  const togli = useMutation({
    mutationFn: (id: string) =>
      mustOk(api.DELETE('/api/v1/orgs/{slug}/channels/{id}', { params: { path: { slug, id } } })),
    onSuccess: () => {
      invalida();
      avvisa(t('Recapito tolto'));
    },
  });

  const pronto = label.trim() !== '' && url.trim().startsWith('https://') && !aggiungi.isPending;

  return (
    <Card variant="outlined">
      <div className="md-section-head">
        <h2 className="md-title-medium">{t('Dove arrivano gli avvisi')}</h2>
        <Button variant="tonal" icon={<Warn />} onClick={() => setApri(true)}>{t('Aggiungi un recapito')}</Button>
      </div>

      {canali.length === 0 ? (
        <Banner tone="error" title={t('Nessun recapito: gli avvisi restano qui dentro')}>
          {t('Molo si accorge di un disco pieno, di un server che smette di riportare o di un certificato che scade, ma per saperlo bisogna aprire il pannello. Un recapito è ciò che fa arrivare la notizia a qualcuno.')}
        </Banner>
      ) : (
        <List>
          {canali.map((c) => (
            <ListItem
              key={c.id}
              headline={c.label}
              supporting={
                c.lastError
                  ? t('{tipo} · non consegna: {errore}', { tipo: c.kind, errore: c.lastError })
                  : c.lastOkAt
                    ? t('{tipo} · ha consegnato {quando}', { tipo: c.kind, quando: quandoRelativo(c.lastOkAt) })
                    : t('{tipo} · non ha ancora avuto niente da consegnare', { tipo: c.kind })
              }
              trailing={
                <Button variant="text" disabled={togli.isPending} onClick={() => togli.mutate(c.id)}>
                  {t('Togli')}
                </Button>
              }
            />
          ))}
        </List>
      )}

      <Details summary={t('Cosa viene guardato')}>
        <p className="md-body-small" style={{ margin: 0 }}>
          {t('Un server che smette di riportare per cinque minuti, un disco o gli inode oltre il novanta per cento, un’istanza che non risponde per cinque minuti, un certificato a quattordici giorni dalla scadenza, una registrazione Enterprise a trenta.')}
        </p>
        <p className="md-body-small" style={{ margin: 0, color: 'var(--md-sys-color-on-surface-variant)' }}>
          {t('Le soglie sono nel codice e non si cambiano da qui: una versione con regole modificabili vuole una schermata di gestione e il caso di chi si disattiva un avviso e poi si lamenta che non è arrivato. Quando servirà una soglia diversa, quella richiesta dirà anche che forma deve avere la configurazione. Durante i lavori dell’hub su un server gli avvisi di quel server tacciono.')}
        </p>
      </Details>

      <Dialog
        open={apri}
        onOpenChange={(v) => !v && setApri(false)}
        title={t('Aggiungi un recapito')}
        description={t('Prima di salvarlo ci mando un messaggio di prova: un canale che non consegna si scopre la notte in cui serviva.')}
        actions={
          <>
            <Button variant="text" onClick={() => setApri(false)}>{t('Annulla')}</Button>
            <Button
              variant="filled"
              icon={aggiungi.isPending ? <Anello /> : undefined}
              disabled={!pronto}
              onClick={() => aggiungi.mutate()}
            >
              {aggiungi.isPending ? t('Provo…') : t('Prova e aggiungi')}
            </Button>
          </>
        }
      >
        <form className="md-form" onSubmit={(e) => { e.preventDefault(); if (pronto) aggiungi.mutate(); }}>
          {aggiungi.error && (
            <Banner tone="error" title={t('Non l’ho aggiunto')}>{(aggiungi.error as Error).message}</Banner>
          )}
          <Select
            label={t('Tipo')}
            value={kind}
            options={[
              { value: 'slack', label: t('Slack — un webhook in entrata') },
              { value: 'ntfy', label: t('ntfy — l’indirizzo di un topic') },
              { value: 'webhook', label: t('Webhook — il nostro JSON, a chi se lo legge') },
            ]}
            onChange={(e) => setKind(e.target.value as 'slack' | 'ntfy' | 'webhook')}
          />
          <TextField label={t('Nome')} value={label} autoFocus onChange={(e) => setLabel(e.target.value)}
            placeholder={t('#infrastruttura')} help={t('Serve solo a riconoscerlo in questo elenco.')} />
          <TextField label={t('Indirizzo')} value={url} onChange={(e) => setUrl(e.target.value)}
            placeholder="https://hooks.slack.com/services/…"
            help={t('Solo https: qui sopra passano i nomi dei tuoi server e dei tuoi clienti. Non viene più mostrato.')} />
        </form>
      </Dialog>
    </Card>
  );
}

/**
 * Le credenziali cloud dell'organizzazione: ogni azienda ha il suo progetto
 * OVH, e la fattura è la sua. Con una credenziale qui, un server si crea
 * dal pannello invece che dalla console del fornitore — e la corsa «il
 * server è perso» può crearselo da sola.
 */
export function CredenzialiCloud({ slug }: { slug: string }) {
  const qc = useQueryClient();
  const avvisa = useSnackbar();
  const q = useQuery({
    queryKey: ['cloud-credentials', slug],
    queryFn: () => must(api.GET('/api/v1/orgs/{slug}/cloud-credentials', { params: { path: { slug } } })),
  });
  const credenziali = q.data?.credentials ?? [];

  const [apri, setApri] = useState(false);
  const [label, setLabel] = useState('');
  const [endpoint, setEndpoint] = useState<'ovh-eu' | 'ovh-ca' | 'ovh-us'>('ovh-eu');
  const [ak, setAk] = useState('');
  const [as, setAs] = useState('');
  const [ck, setCk] = useState('');
  const [project, setProject] = useState('');
  const [daAggiornare, setDaAggiornare] = useState<{ id: string; label: string } | null>(null);

  const invalida = () => qc.invalidateQueries({ queryKey: ['cloud-credentials', slug] });

  const aggiungi = useMutation({
    mutationFn: () =>
      must(api.POST('/api/v1/orgs/{slug}/cloud-credentials', {
        params: { path: { slug } },
        body: {
          provider: 'ovh', label: label.trim(), endpoint,
          applicationKey: ak.trim(), applicationSecret: as.trim(), consumerKey: ck.trim(), project: project.trim(),
        },
      })),
    onSuccess: (c) => {
      invalida();
      setApri(false);
      setLabel(''); setAk(''); setAs(''); setCk(''); setProject('');
      avvisa(t('{nome} collegata: OVH ha riconosciuto le chiavi', { nome: c.label }));
    },
  });

  const togli = useMutation({
    mutationFn: (id: string) =>
      mustOk(api.DELETE('/api/v1/orgs/{slug}/cloud-credentials/{id}', { params: { path: { slug, id } } })),
    onSuccess: () => {
      invalida();
      avvisa(t('Credenziale tolta'));
    },
    onError: (e: Error) => avvisa(e.message),
  });

  const pronto = label.trim() !== '' && ak.trim() !== '' && as.trim() !== '' && ck.trim() !== '' && project.trim() !== '' && !aggiungi.isPending;

  return (
    <Card variant="outlined">
      <div className="md-section-head">
        <h2 className="md-title-medium">{t('Progetto OVH')}</h2>
        <Button variant="tonal" onClick={() => setApri(true)}>{t('Collega un progetto')}</Button>
      </div>

      {q.isSuccess && credenziali.length === 0 ? (
        <p className="md-body-medium" style={{ margin: 0, color: 'var(--md-sys-color-on-surface-variant)' }}>
          {t('Con le chiavi API del progetto Public Cloud dell’azienda, un server si crea da qui invece che dalla console di OVH — e «{bottone}» può crearselo da solo. Senza, i server si collegano a mano, come adesso.', {
            bottone: t('Il server è perso'),
          })}
        </p>
      ) : (
        <List>
          {credenziali.map((c) => (
            <ListItem
              key={c.id}
              headline={c.label}
              supporting={
                c.serversInUse === 0
                  ? t('{provider} · nessun server creati con questa', { provider: c.provider })
                  : c.serversInUse === 1
                    ? t('{provider} · un server creati con questa', { provider: c.provider })
                    : t('{provider} · {n} server creati con questa', { provider: c.provider, n: c.serversInUse })
              }
              trailing={
                <span className="md-stack__inline">
                  {c.provider === 'ovh' && (
                    <Button variant="text" onClick={() => setDaAggiornare({ id: c.id, label: c.label })}>
                      {t('Aggiorna le chiavi')}
                    </Button>
                  )}
                  <Button variant="text" disabled={togli.isPending || c.serversInUse > 0} onClick={() => togli.mutate(c.id)}>
                    {t('Togli')}
                  </Button>
                </span>
              }
            />
          ))}
        </List>
      )}

      <Details summary={t('Come si fanno le chiavi su OVH')}>
        <p className="md-body-small" style={{ margin: 0 }}>
          {t('Da {url}, con l’account che possiede il progetto: nome e descrizione a piacere, validità {illimitata} (non «1 day»), e come diritti tre righe, {get}, {post} e {delete}, tutte su {percorso}.', {
            url: <span className="md-mono">eu.api.ovh.com/createToken/</span>,
            illimitata: <b>{t('illimitata')}</b>,
            get: <span className="md-mono">GET</span>,
            post: <span className="md-mono">POST</span>,
            delete: <span className="md-mono">DELETE</span>,
            percorso: <span className="md-mono">{t('/cloud/project/<id del progetto>/*')}</span>,
          })}{' '}
          {t('Per far comprare a Molo l’IP fisso dei server nuovi servono anche {me}, {cart} e {cartTutto}, e sull’account un metodo di pagamento predefinito.', {
            me: <span className="md-mono">GET /me</span>,
            cart: <span className="md-mono">POST /order/cart</span>,
            cartTutto: <span className="md-mono">POST /order/cart/*</span>,
          })}{' '}
          {t('Gli IP ristretti vanno lasciati vuoti: un’istanza con IP flottante esce con l’IP nativo, non col flottante. Ne escono tre chiavi; l’id del progetto è nell’indirizzo della console del Public Cloud. Le chiavi si provano prima di essere salvate e non vengono più mostrate.')}
        </p>
      </Details>

      <Dialog
        open={apri}
        onOpenChange={(v) => !v && setApri(false)}
        title={t('Collega un progetto OVH')}
        description={t('Le tre chiavi di api.ovh.com/createToken e l’id del progetto. Vengono provate contro OVH prima di essere salvate.')}
        actions={
          <>
            <Button variant="text" onClick={() => setApri(false)}>{t('Annulla')}</Button>
            <Button variant="filled" icon={aggiungi.isPending ? <Anello /> : undefined} disabled={!pronto} onClick={() => aggiungi.mutate()}>
              {aggiungi.isPending ? t('Provo…') : t('Prova e collega')}
            </Button>
          </>
        }
      >
        <form className="md-form" onSubmit={(e) => { e.preventDefault(); if (pronto) aggiungi.mutate(); }}>
          {aggiungi.error && <Banner tone="error" title={t('Non l’ho collegato')}>{(aggiungi.error as Error).message}</Banner>}
          <TextField label={t('Nome')} value={label} autoFocus onChange={(e) => setLabel(e.target.value)} placeholder={t('Public Cloud di Acme')} help={t('Serve solo a riconoscerlo.')} />
          <Select label="API" value={endpoint} options={[{ value: 'ovh-eu', label: t('Europa (eu.api.ovh.com)') }, { value: 'ovh-ca', label: 'Canada' }, { value: 'ovh-us', label: t('Stati Uniti') }]} onChange={(e) => setEndpoint(e.target.value as typeof endpoint)} />
          <TextField label={t('Id del progetto Public Cloud')} value={project} onChange={(e) => setProject(e.target.value)} help={t('Il serviceName: 32 caratteri esadecimali, nell’indirizzo della console.')} />
          <TextField label="Application key" value={ak} onChange={(e) => setAk(e.target.value)} />
          <TextField label="Application secret" value={as} type="password" onChange={(e) => setAs(e.target.value)} />
          <TextField label="Consumer key" value={ck} type="password" onChange={(e) => setCk(e.target.value)} />
        </form>
      </Dialog>

      {daAggiornare && <AggiornaChiavi slug={slug} credenziale={daAggiornare} onClose={() => setDaAggiornare(null)} />}
    </Card>
  );
}

/**
 * Chiavi nuove per una credenziale già collegata: su OVH i diritti di una
 * chiave non si cambiano, se ne fa un'altra. La credenziale resta la stessa,
 * quindi i server creati con lei passano alle chiavi nuove senza toccarli.
 */
function AggiornaChiavi({
  slug, credenziale, onClose,
}: { slug: string; credenziale: { id: string; label: string }; onClose: () => void }) {
  const qc = useQueryClient();
  const avvisa = useSnackbar();
  const [ak, setAk] = useState('');
  const [as, setAs] = useState('');
  const [ck, setCk] = useState('');

  const aggiorna = useMutation({
    mutationFn: () =>
      must(api.PUT('/api/v1/orgs/{slug}/cloud-credentials/{id}/keys', {
        params: { path: { slug, id: credenziale.id } },
        body: { applicationKey: ak.trim(), applicationSecret: as.trim(), consumerKey: ck.trim() },
      })),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['cloud-credentials', slug] });
      onClose();
      avvisa(t('Chiavi di {nome} aggiornate: OVH le ha riconosciute', { nome: credenziale.label }));
    },
  });
  const pronto = ak.trim() !== '' && as.trim() !== '' && ck.trim() !== '' && !aggiorna.isPending;

  return (
    <Dialog
      open
      onOpenChange={(v) => !v && onClose()}
      title={t('Aggiorna le chiavi di {nome}', { nome: credenziale.label })}
      description={t('Le tre chiavi nuove di api.ovh.com/createToken, dello stesso progetto. Vengono provate contro OVH prima di prendere il posto delle vecchie, che vengono cancellate.')}
      actions={
        <>
          <Button variant="text" onClick={onClose}>{t('Annulla')}</Button>
          <Button variant="filled" icon={aggiorna.isPending ? <Anello /> : undefined} disabled={!pronto} onClick={() => aggiorna.mutate()}>
            {aggiorna.isPending ? t('Provo…') : t('Prova e aggiorna')}
          </Button>
        </>
      }
    >
      <form className="md-form" onSubmit={(e) => { e.preventDefault(); if (pronto) aggiorna.mutate(); }}>
        {aggiorna.error && <Banner tone="error" title={t('Chiavi non aggiornate')}>{(aggiorna.error as Error).message}</Banner>}
        <TextField label="Application key" value={ak} autoFocus onChange={(e) => setAk(e.target.value)} />
        <TextField label="Application secret" value={as} type="password" onChange={(e) => setAs(e.target.value)} />
        <TextField label="Consumer key" value={ck} type="password" onChange={(e) => setCk(e.target.value)} />
      </form>
    </Dialog>
  );
}
